import React, { ReactElement, useMemo } from 'react';
import { Field, FormikTouched, getIn } from 'formik';
import { useItemCreationContext } from '~/ModularComponents/ItemCreation/ItemCreation';
import { TextField } from '~/UIKit';
import { ItemCreationFieldValue } from '~/ModularComponents/ItemCreation/ItemCreation.models';
import { CHARCOAL_GREY } from '~/Constants/Colors.constants';
import { makeStyles } from '~/UIKit/Style';

interface IInputNumberProps<T> {
  name: keyof T;
  icon?: ReactElement;
  label: string;
}

const useStyles = makeStyles(
  {
    container: {
      color: CHARCOAL_GREY,
      width: '100%',
    },
  },
  { name: 'inputNumber' },
);

const handleBlurNumbers = <T extends object>({
  setFieldValue,
  setTouched,
  name,
  value,
  touched,
}: {
  setFieldValue: ItemCreationFieldValue;
  setTouched: (touched: any, shouldValidate?: boolean) => void;
  value: number;
  name: keyof T;
  touched: FormikTouched<T>;
}) => () => {
  setTouched({ ...touched, [name]: true }, true);
  if (!value) {
    setFieldValue(name as string, 0);
  }
};

export const InputNumber = <T extends object>({ name, icon, label }: IInputNumberProps<T>) => {
  const {
    isLoading,
    disabled,
    values,
    touched,
    setFieldValue,
    errors,
    setTouched,
  } = useItemCreationContext();
  const { classes } = useStyles();

  const value = getIn(values, name as string);
  const isDisabled = getIn(disabled, name as string);
  const isTouched = getIn(touched, name as string);
  const error = getIn(errors, name as string);

  return useMemo(
    () => (
      <Field
        key={name}
        className={classes.container}
        component={TextField}
        fullWidth
        name={name}
        value={value}
        type="number"
        label={label}
        disabled={isDisabled || isLoading}
        error={isTouched && !!error}
        newDesign
        icon={icon}
        onBlur={handleBlurNumbers<T>({ setFieldValue, setTouched, value, name, touched })}
      />
    ),
    [value, isDisabled, isLoading, isTouched, error, icon],
  );
};
